import classNames from 'classnames';
import { Fragment, useId, useMemo, useState, type ReactNode } from 'react';
import { LoadingSpinner } from '~/components';
import { MarketDetailItem, MarketStatusPill } from '~/features/shared/market-content';

export type NegotiationTermGroup = { title: string; terms: readonly string[] };
export type NegotiationParty = 'YOU' | 'THEM';

type Headline = {
  shifts?: string | null;
  ftes?: string | null;
  rate?: string | null;
  value?: string | null;
};

/** One round of a negotiation, already reduced to what the history renders. */
export type NegotiationHistoryVersion = {
  id: string | number;
  version: number | null;
  /** The raw sender value; compared with the `viewerSender` prop to read "You" or "Them". */
  sender: string | null;
  createdAt: string | null;
  headline: Headline;
  terms: Record<string, unknown>;
  /** Extra detail shown under the terms, such as the selected availability. */
  extra?: ReactNode;
};

export type NegotiationHistoryLatest = {
  version: number;
  party: NegotiationParty | null;
  headline: Headline;
};

export const negotiationParty = (
  sender: string | null | undefined,
  viewerSender: string
): NegotiationParty | null => (sender == null ? null : sender === viewerSender ? 'YOU' : 'THEM');

type Props = {
  rounds: number;
  latest: NegotiationHistoryLatest | null;
  viewerSender: string;
  /** Null until the rounds are read; the table shows the loading or error state meanwhile. */
  versions: NegotiationHistoryVersion[] | null;
  loading?: boolean;
  error?: string | null;
  /** Controlled when `expanded` is given; otherwise the history keeps its own toggle. */
  expanded?: boolean;
  defaultExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  /** Start the newest round's terms open, for a reader who is about to accept them. */
  defaultOpenLatest?: boolean;
  termGroups: readonly NegotiationTermGroup[];
  termLabel?: (key: string) => string;
  formatTerm?: (key: string, value: unknown) => string;
  emptyMessage?: ReactNode;
  cy?: { card?: string; toggle?: string; history?: string; row?: string; header?: string };
};

const defaultLabel = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2');
const defaultFormat = (_key: string, value: unknown) =>
  typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

const normalize = (value: unknown) => (value == null ? '' : String(value).trim());
const sameTerm = (left: unknown, right: unknown) => {
  const a = normalize(left);
  const b = normalize(right);
  if (a === b) return true;
  const numeric = a !== '' && b !== '' && Number.isFinite(Number(a)) && Number.isFinite(Number(b));
  return numeric && Number(a) === Number(b);
};

// [MCV-1794] A round's createdAt is a moment in time, so its day is the viewer's: a round made at
// 8 pm Pacific on Oct 2 is 03:00Z on Oct 3, and printing it in UTC showed the wrong day. Only a
// date-only value (YYYY-MM-DD) has no time to convert and keeps UTC, or it would slip back a day
// west of Greenwich.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const formatDay = (value: string | null) => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isNaN(parsed)
    ? '—'
    : new Date(parsed).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(DATE_ONLY.test((value as string).trim()) ? { timeZone: 'UTC' } : {}),
      });
};

type TermRow = { key: string; label: string; text: string; changed: boolean; previous: string };
type TermSection = { title: string; rows: TermRow[] };

const buildSections = (
  groups: readonly NegotiationTermGroup[],
  terms: Record<string, unknown>,
  previous: Record<string, unknown> | null,
  label: (key: string) => string,
  format: (key: string, value: unknown) => string
): TermSection[] =>
  groups.flatMap((group) => {
    const rows = group.terms.flatMap((key): TermRow[] => {
      const current = format(key, terms[key]);
      const isSet = terms[key] != null && current.trim() !== '';
      const changed = previous != null && !sameTerm(terms[key], previous[key]);
      if (!isSet && !changed) return [];
      const before = previous != null && previous[key] != null ? format(key, previous[key]) : '';
      return [
        {
          key,
          label: label(key),
          text: isSet ? current : 'Not specified',
          changed,
          previous: before.trim() === '' ? 'Not specified' : before,
        },
      ];
    });
    return rows.length > 0 ? [{ title: group.title, rows }] : [];
  });

const TermSections = ({ sections }: { sections: TermSection[] }) => (
  <div className="space-y-4">
    {sections.map((section) => (
      <section key={section.title} aria-label={section.title}>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">
          {section.title}
        </h4>
        <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-x-6 gap-y-3">
          {section.rows.map((row) => (
            <div
              key={row.key}
              className="min-w-0 border-b border-grey-900 pb-2"
              data-cy={`negotiation-term-${row.key}`}
              data-testid={`negotiation-term-${row.key}`}
            >
              <dt className="flex flex-wrap items-center gap-2 text-sm font-semibold text-content-secondary">
                {row.label}
                {row.changed && <MarketStatusPill status="warning" label="Changed" />}
              </dt>
              <dd className="mt-1 whitespace-pre-line text-sm font-medium text-content-primary [overflow-wrap:anywhere]">
                {row.text}
              </dd>
              {row.changed && (
                <dd className="mt-0.5 whitespace-pre-line text-xs text-content-secondary [overflow-wrap:anywhere]">
                  Previously: <span className="line-through">{row.previous}</span>
                </dd>
              )}
            </div>
          ))}
        </dl>
      </section>
    ))}
  </div>
);

/**
 * The negotiation card and its history, shared by every response page: a
 * summary of the latest round, and on request a table of every round whose
 * rows open to the non-empty terms, with what changed since the round before.
 */
export const NegotiationHistory = ({
  rounds,
  latest,
  viewerSender,
  versions,
  loading = false,
  error = null,
  expanded,
  defaultExpanded = false,
  onToggle,
  defaultOpenLatest = false,
  termGroups,
  termLabel = defaultLabel,
  formatTerm = defaultFormat,
  emptyMessage = 'No negotiation started.',
  cy = {},
}: Props) => {
  const panelBase = useId();
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});
  const showHistory = expanded ?? localExpanded;
  const toggle = () => {
    onToggle?.(!showHistory);
    setLocalExpanded(!showHistory);
  };

  const rows = useMemo(() => {
    const ascending = [...(versions ?? [])].sort((a, b) => (a.version ?? 0) - (b.version ?? 0));
    return ascending
      .map((entry, index) => {
        const previous = index > 0 ? ascending[index - 1] : null;
        const sections = buildSections(
          termGroups,
          entry.terms,
          previous?.terms ?? null,
          termLabel,
          formatTerm
        );
        return { entry, sections, previousVersion: previous?.version ?? null };
      })
      .reverse();
  }, [versions, termGroups, termLabel, formatTerm]);

  const showShifts = rows.some(({ entry }) => entry.headline.shifts != null);
  const showFtes = rows.some(({ entry }) => entry.headline.ftes != null);
  const latestRowId = rows[0]?.entry.id;
  const isOpen = (id: string | number) =>
    openOverride[String(id)] ?? (defaultOpenLatest && id === latestRowId);
  const columnCount = 5 + (showShifts ? 1 : 0) + (showFtes ? 1 : 0);

  return (
    <div
      className="rounded-lg border border-grey-900 p-4 text-left"
      data-cy={cy.card ?? 'negotiation-summary'}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-left text-[1rem] font-semibold text-content-primary">Negotiation</h3>
          <p className="mt-1 text-left text-[1rem] text-content-secondary">
            {rounds} {rounds === 1 ? 'round' : 'rounds'}
          </p>
        </div>
        {rounds > 0 && (
          <button
            type="button"
            aria-expanded={showHistory}
            className="text-sm font-semibold text-primary-50 hover:underline"
            onClick={toggle}
            data-cy={cy.toggle ?? 'btn-negotiation-history'}
          >
            {showHistory ? 'Hide history' : 'View history'}
          </button>
        )}
      </div>
      {latest ? (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-3">
          <MarketDetailItem label="Version" value={`v${latest.version}`} />
          {latest.party && (
            <MarketDetailItem label="Last Sender" value={latest.party === 'YOU' ? 'You' : 'Them'} />
          )}
          {latest.headline.shifts != null && (
            <MarketDetailItem label="Shifts" value={latest.headline.shifts} />
          )}
          {latest.headline.ftes != null && (
            <MarketDetailItem label="FTEs" value={latest.headline.ftes} />
          )}
          {latest.headline.rate && <MarketDetailItem label="Rate" value={latest.headline.rate} />}
          {latest.headline.value && (
            <MarketDetailItem label="Value" value={latest.headline.value} />
          )}
        </div>
      ) : (
        <p className="text-left text-[1rem] text-content-secondary">{emptyMessage}</p>
      )}
      {showHistory && (
        <div
          className="mt-4 rounded-md border border-grey-900"
          data-cy={cy.history ?? 'negotiation-history'}
        >
          {loading && (
            <div className="flex justify-center p-4">
              <LoadingSpinner />
            </div>
          )}
          {error && (
            <p className="p-3 text-sm text-error" data-cy="full-history-error">
              {error}
            </p>
          )}
          {!loading && !error && rows.length === 0 && (
            <p className="p-3 text-left text-[1rem] text-content-secondary">No history returned.</p>
          )}
          {!loading && !error && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                <caption className="sr-only">Negotiation history, newest round first</caption>
                <thead>
                  <tr
                    className="border-b border-grey-900 bg-grey-950 text-xs uppercase tracking-wide text-content-secondary"
                    data-cy={cy.header ?? 'negotiation-table-header'}
                  >
                    <th scope="col" className="px-3 py-2 font-semibold">
                      Ver.
                    </th>
                    <th scope="col" className="px-3 py-2 font-semibold">
                      Party
                    </th>
                    {showShifts && (
                      <th scope="col" className="px-3 py-2 font-semibold">
                        Shifts
                      </th>
                    )}
                    {showFtes && (
                      <th scope="col" className="px-3 py-2 font-semibold">
                        FTEs
                      </th>
                    )}
                    <th scope="col" className="px-3 py-2 font-semibold">
                      Rate
                    </th>
                    <th scope="col" className="px-3 py-2 font-semibold">
                      Value
                    </th>
                    <th scope="col" className="px-3 py-2 font-semibold">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ entry, sections, previousVersion }) => {
                    const party = negotiationParty(entry.sender, viewerSender);
                    const hasDetail = sections.length > 0 || entry.extra != null;
                    const open = hasDetail && isOpen(entry.id);
                    const panelId = `${panelBase}-${entry.id}`;
                    const changedCount = sections.reduce(
                      (sum, section) => sum + section.rows.filter((row) => row.changed).length,
                      0
                    );
                    return (
                      <Fragment key={entry.id}>
                        <tr
                          // [MCV-1805] Each round's summary row is tinted so the vN lines stand
                          // out from the white terms panel opened under them.
                          className="border-b border-grey-900 bg-primary-t-900 last:border-b-0"
                          data-cy={cy.row ?? 'negotiation-row'}
                        >
                          <th scope="row" className="px-3 py-3 font-semibold text-primary-50">
                            {hasDetail ? (
                              <button
                                type="button"
                                aria-expanded={open}
                                aria-controls={open ? panelId : undefined}
                                aria-label={`v${entry.version ?? '—'} terms`}
                                className="inline-flex items-center gap-1 font-semibold text-primary-50 hover:underline"
                                onClick={() =>
                                  setOpenOverride((current) => ({
                                    ...current,
                                    [String(entry.id)]: !open,
                                  }))
                                }
                                data-cy="btn-negotiation-version-terms"
                              >
                                <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                                {`v${entry.version ?? '—'}`}
                              </button>
                            ) : (
                              `v${entry.version ?? '—'}`
                            )}
                          </th>
                          <td className="px-3 py-3">
                            <div className="flex min-w-0 items-center gap-2">
                              {party && (
                                <span
                                  className={classNames(
                                    'rounded-full px-[10px] py-[2px] text-xs font-semibold',
                                    // White, not the row's own tint, or the pill disappears into it.
                                    party === 'YOU'
                                      ? 'bg-white text-primary-50'
                                      : 'bg-grey-950 text-content-secondary'
                                  )}
                                >
                                  {party === 'YOU' ? 'You' : 'Them'}
                                </span>
                              )}
                              <span className="truncate text-grey-600">
                                {entry.version === 1 ? 'Initial offer' : 'Counteroffer'}
                              </span>
                            </div>
                          </td>
                          {showShifts && (
                            <td className="px-3 py-3 font-semibold text-content-secondary">
                              {entry.headline.shifts ?? '—'}
                            </td>
                          )}
                          {showFtes && (
                            <td className="px-3 py-3 font-medium text-content-secondary">
                              {entry.headline.ftes ?? '—'}
                            </td>
                          )}
                          <td className="px-3 py-3 font-medium text-content-secondary">
                            {entry.headline.rate || '—'}
                          </td>
                          <td className="px-3 py-3 font-medium text-primary-50">
                            {entry.headline.value ?? '—'}
                          </td>
                          <td className="px-3 py-3 text-grey-600">{formatDay(entry.createdAt)}</td>
                        </tr>
                        {open && (
                          <tr id={panelId} className="border-b border-grey-900 bg-grey-950/50">
                            <td colSpan={columnCount} className="px-4 py-4">
                              {previousVersion != null && (
                                <p className="mb-3 text-xs text-content-secondary">
                                  {changedCount === 0
                                    ? `No terms changed from v${previousVersion}.`
                                    : `${changedCount} ${changedCount === 1 ? 'term' : 'terms'} changed from v${previousVersion}.`}
                                </p>
                              )}
                              <TermSections sections={sections} />
                              {entry.extra != null && (
                                <div className={sections.length > 0 ? 'mt-4' : undefined}>
                                  {entry.extra}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
